import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { Alert, Avatar, Button, Card, Chip, InputAdornment, InputBase, Skeleton, Typography } from '@mui/material'
import { Link, useNavigate } from '@tanstack/react-router'
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { PageBackHeader } from '@/components/PageBackHeader/PageBackHeader'
import { useAuth } from '@/features/auth/AuthProvider'
import type { ConfirmationStatus } from '@/features/registrations/registrationService'
import { smsService, type SmsDeliveryStatus, type SmsService } from '@/features/sms/smsService'
import { compareContacts, initialsFor, letterFor, matchesKey, searchKey, searchQuery, type SearchKey } from '../../contactOrder'
import { useWindowVirtualizer } from '../../useWindowVirtualizer'
import { useMyContacts, type MyContactsSources } from '../../useMyContacts'
import styles from './MyContactsPage.module.scss'

/** One row of My contacts: a sent registration, or one still on this device. */
type Entry = Readonly<{
  id: string
  firstName: string
  lastName: string | null
  phoneNumber: string | null
  emailAddress: string | null
  eventId: string
  eventName: string | null
  status: ConfirmationStatus | null
  onDevice: boolean
  letter: string
  key: SearchKey
}>

/** A drawn line of the list: a letter heading or a contact. Heights match the stylesheet. */
type Line = Readonly<{ kind: 'letter'; letter: string } | { kind: 'contact'; entry: Entry }>
const letterHeight = 34
const rowHeight = 57

const savedFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const failed = new Set(['not_delivered', 'not_sent'])

function Mark({ entry, delivery }: Readonly<{ entry: Entry; delivery: SmsDeliveryStatus | undefined }>) {
  if (entry.onDevice) return <span className={styles.notSent}>Not sent yet</span>
  if (delivery && failed.has(delivery.state)) return <span className={`${styles.mark} ${styles.warn}`}><ReportProblemOutlinedIcon fontSize="small" aria-label="Text not delivered" /></span>
  if (entry.status === 'confirmed') return <span className={`${styles.mark} ${styles.confirmed}`}><CheckIcon fontSize="small" aria-label="Confirmed" /></span>
  if (entry.status === 'unreachable') return <span className={`${styles.mark} ${styles.unreachable}`}><CloseIcon fontSize="small" aria-label="Unreachable" /></span>
  return <span className={`${styles.mark} ${styles.maybe}`}><FavoriteIcon fontSize="small" aria-label="Maybe" /></span>
}

/**
 * Everyone you've registered, at every event, in one alphabetical list like a
 * phone's contacts. Each registration is its own entry: two people who share
 * a number are still two contacts.
 */
export function MyContactsPage({ sources, sms = smsService }: Readonly<{ sources?: MyContactsSources; sms?: Pick<SmsService, 'readStatuses'> }>) {
  const auth = useAuth()
  const navigate = useNavigate()
  const userId = auth.session?.user.id ?? null
  const contacts = useMyContacts(userId, sources)
  const [query, setQuery] = useState('')
  const [eventFilter, setEventFilter] = useState<string | null>(null)
  const [statuses, setStatuses] = useState<Readonly<Record<string, SmsDeliveryStatus>>>({})

  useEffect(() => {
    if (contacts.loading || contacts.savedAt || contacts.contacts.length === 0) return
    let active = true
    void sms.readStatuses(contacts.contacts.map((contact) => contact.registrationId)).then(
      (read) => { if (active) setStatuses(read) },
      () => undefined,
    )
    return () => { active = false }
  }, [contacts.contacts, contacts.loading, contacts.savedAt, sms])

  const eventNames = useMemo(() => new Map(contacts.contacts.map((contact) => [contact.eventId, contact.eventName])), [contacts.contacts])
  // Sorted, lettered and made searchable once per load, not on every keystroke.
  const entries = useMemo<Entry[]>(() => {
    const raw = [
      ...contacts.onDevice.map((record) => ({
        id: record.clientRegistrationId, firstName: record.contact.firstName, lastName: record.contact.lastName ?? null,
        phoneNumber: record.contact.phoneNumber ?? null, emailAddress: record.contact.emailAddress ?? null,
        eventId: record.eventId, eventName: eventNames.get(record.eventId) ?? null, status: null, onDevice: true,
      })),
      ...contacts.contacts.map((contact) => ({
        id: contact.registrationId, firstName: contact.firstName, lastName: contact.lastName,
        phoneNumber: contact.phoneNumber, emailAddress: contact.emailAddress,
        eventId: contact.eventId, eventName: contact.eventName, status: contact.confirmationStatus, onDevice: false,
      })),
    ]
    const sorted = raw.map((entry) => ({ ...entry, letter: letterFor(entry), key: searchKey(entry) })).sort(compareContacts)
    // # sorts after Z, as on a phone.
    return [...sorted.filter((entry) => entry.letter !== '#'), ...sorted.filter((entry) => entry.letter === '#')]
  }, [contacts.contacts, contacts.onDevice, eventNames])

  // One chip per event, newest event first.
  const events = useMemo(() => {
    const seen = new Map<string, { name: string; date: string }>()
    for (const contact of contacts.contacts) if (!seen.has(contact.eventId)) seen.set(contact.eventId, { name: contact.eventName, date: contact.eventDate ?? '' })
    return [...seen.entries()].sort(([, first], [, second]) => second.date.localeCompare(first.date))
  }, [contacts.contacts])

  // Typing stays responsive: the list catches up with the search a moment later.
  const deferredQuery = useDeferredValue(query)
  const shown = useMemo(() => {
    const wanted = searchQuery(deferredQuery)
    return entries.filter((entry) => (!eventFilter || entry.eventId === eventFilter) && matchesKey(entry.key, wanted))
  }, [entries, eventFilter, deferredQuery])
  const { lines, letters } = useMemo(() => {
    const built: Line[] = []
    const starts = new Map<string, number>()
    for (const entry of shown) {
      if (!starts.has(entry.letter)) { starts.set(entry.letter, built.length); built.push({ kind: 'letter', letter: entry.letter }) }
      built.push({ kind: 'contact', entry })
    }
    return { lines: built, letters: starts }
  }, [shown])
  const heights = useMemo(() => lines.map((line) => (line.kind === 'letter' ? letterHeight : rowHeight)), [lines])
  const { attach, total: listHeight, items: drawn, scrollToIndex } = useWindowVirtualizer(heights)

  const header = <PageBackHeader backLabel="Outreach" onBack={() => void navigate({ to: '/sessions' })}><h1 className={styles.title}>My contacts</h1></PageBackHeader>
  if (contacts.loading) return <section className={styles.page}>{header}<div role="status" aria-label="Loading your contacts"><Skeleton height={48} /><Skeleton height={56} /><Skeleton height={56} /><Skeleton height={56} /></div></section>

  const tryAgain = <Button color="inherit" size="small" onClick={contacts.reload}>Try again</Button>
  return (
    <section className={styles.page}>
      {header}
      {contacts.error && !contacts.savedAt && contacts.total === 0 ? <Alert severity="error" action={tryAgain}>{contacts.error}</Alert> : null}
      {contacts.savedAt ? <Typography className={styles.note}>As saved on this device ({savedFormat.format(new Date(contacts.savedAt))}).</Typography> : null}
      {contacts.total === 0 && !contacts.error ? (
        <Card className={styles.empty}><Typography>No contacts yet.</Typography><Typography color="text.secondary">Everyone you register, at any event, appears here.</Typography></Card>
      ) : null}
      {contacts.total > 0 ? (
        <>
          <InputBase
            className={styles.search}
            placeholder="Search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Search your contacts', type: 'search' }}
            startAdornment={<InputAdornment position="start"><SearchIcon className={styles.searchIcon} /></InputAdornment>}
          />
          {events.length > 1 ? (
            <div className={styles.chips} role="group" aria-label="Show contacts from">
              <Chip label="All events" aria-pressed={eventFilter === null} className={eventFilter === null ? styles.chipOn : undefined} variant={eventFilter === null ? 'filled' : 'outlined'} onClick={() => setEventFilter(null)} />
              {events.map(([id, event]) => (
                <Chip key={id} label={event.name} aria-pressed={eventFilter === id} className={eventFilter === id ? styles.chipOn : undefined} variant={eventFilter === id ? 'filled' : 'outlined'} onClick={() => setEventFilter(eventFilter === id ? null : id)} />
              ))}
            </div>
          ) : null}
          {shown.length === 0 ? <Typography className={styles.note}>No contacts match.</Typography> : (
            <div className={styles.listWrap}>
              {/* Only the lines on or near the screen are drawn; the rest is empty space of the right height. */}
              <div ref={attach} className={styles.virtual} style={{ height: listHeight }}>
                <ul className={styles.list} aria-label="Your contacts">
                  {drawn.map(({ index, top, height }) => {
                    const line = lines[index]
                    if (!line) return null
                    return line.kind === 'letter' ? (
                      <li key={`letter-${line.letter}`} className={styles.line} style={{ top, height }}>
                        <h2 className={styles.letter} id={`contacts-${line.letter}`}>{line.letter}</h2>
                      </li>
                    ) : (
                      <li key={line.entry.id} className={styles.line} style={{ top, height }}>
                        <Link className={styles.row} to="/contacts/$contactId" params={{ contactId: line.entry.id }}>
                          <Avatar className={styles.avatar} aria-hidden="true">{initialsFor(line.entry)}</Avatar>
                          <span className={styles.rowText}>
                            <span className={styles.rowName}><b>{line.entry.firstName}</b>{line.entry.lastName ? ` ${line.entry.lastName}` : null}</span>
                            <span className={styles.rowMeta}>{line.entry.eventName ?? 'Waiting to sync'}</span>
                          </span>
                          <Mark entry={line.entry} delivery={statuses[line.entry.id]} />
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
              {letters.size > 1 ? (
                <nav className={styles.rail} aria-label="Jump to letter">
                  {[...letters.entries()].map(([letter, index]) => <button key={letter} type="button" aria-label={`Jump to ${letter}`} onClick={() => scrollToIndex(index)}>{letter}</button>)}
                </nav>
              ) : null}
            </div>
          )}
          <Typography className={styles.count}>{shown.length === 1 ? '1 contact' : `${shown.length.toLocaleString('en-GB')} contacts`}</Typography>
        </>
      ) : null}
    </section>
  )
}
